import { useMemo, useState } from 'react'
import { Button, Card, Chip } from '@heroui/react'
import { SETTING_FIELDS, codeShapeOk, deskLabel, effective, renameDeskCode, retiring } from '../../lib/equipment'
import { designChoices } from '../../lib/kioskProfile'
import { designFields, machineDesign, withDesign } from '../../lib/machineDesign'
import { passUnsaved } from '../../lib/unsaved'
import type { StaffKey } from '../../lib/staffText'
import type { Branch, BranchDoc, ProfileUnit, UnitInstance, UnitInstances, UnitSettings } from '../../lib/types'
import { useSession } from '../../ui/Session'
import { useStaffText } from '../../ui/StaffText'
import { Field, Text } from '../parts'
import { Confirm } from '../sectionKit'
import { EditLayout, FormSection, IconTile, InnerPage, SummaryCard, type Crumb } from '../settingsKit'
import { AskBar, Pick, Toggles } from '../setupKit'
import { Address } from './Address'
import { LiveStatus, removeQuestion, type MachineData } from './BranchEquipment'
import { DesignPicker } from './MachineDesign'
import { ScreenAppSection } from './ScreenAppSection'
import { ScreenKeySection } from './ScreenKeySection'
import { SettingsForm, type InheritedFrom, type SettingField } from './SettingsForm'
import type { BranchDraft } from './useBranchDraft'
import '../../styles/branchmachines.css'

const up = (s: string) => s.toUpperCase()

/**
 * One machine of the branch, as a page: code, name, desk link or desk filter, the
 * design it shows, and its own values over the profile's. Save and Remove are live
 * and return to the branch; the screen app and the key save on their own (§11.6).
 */
export function MachinePage({
  branch,
  d,
  data,
  gi,
  j,
  canWrite,
  crumbs,
  onClose,
  flash,
}: {
  branch: Branch
  d: BranchDraft
  data: MachineData
  gi: number
  j: number
  canWrite: boolean
  crumbs: Crumb[]
  onClose: () => void
  /** The branch page's toast, which outlives this page. */
  flash: (text: string) => void
}) {
  const draft = d.draft!
  const equipment = d.equipment!
  const group = draft.units[gi]!
  const instance = group.instances[j]!
  const pu = equipment.document.units.find((u) => u.id === group.profileUnitId)
  // A machine whose profile unit is gone is only removed, from the list.
  if (!pu) return null
  return (
    <MachineForm
      key={`${gi}:${j}`}
      {...{ branch, d, data, gi, j, canWrite, crumbs, onClose, flash, instance }}
      unit={pu}
      allUnits={draft.units}
      unitKind={(id) => equipment.document.units.find((u) => u.id === id)?.kind}
    />
  )
}

function MachineForm({
  branch,
  d,
  data,
  gi,
  j,
  canWrite,
  crumbs,
  onClose,
  flash,
  instance,
  unit: pu,
  allUnits,
  unitKind,
}: {
  branch: Branch
  d: BranchDraft
  data: MachineData
  gi: number
  j: number
  canWrite: boolean
  crumbs: Crumb[]
  onClose: () => void
  flash: (text: string) => void
  instance: UnitInstance
  unit: ProfileUnit
  allUnits: UnitInstances[]
  unitKind: (profileUnitId: string) => string | undefined
}) {
  const { say } = useStaffText()
  const session = useSession()
  const kind = pu.kind
  const draft = d.draft!
  const defaults = d.equipment!.kindDefaults[kind] ?? {}
  const profileSettings = pu.settings ?? {}
  const [code, setCode] = useState(instance.code)
  const [name, setName] = useState(instance.name)
  const [desk, setDesk] = useState(instance.desk ?? '')
  const [filter, setFilter] = useState<string[]>(instance.deskFilter ?? [])
  const [own, setOwn] = useState<UnitSettings>(instance.overrides ?? {})
  const [asking, setAsking] = useState(false)
  const [refused, setRefused] = useState('')

  const deskInstances = allUnits.filter((g) => unitKind(g.profileUnitId) === 'service_point').flatMap((g) => g.instances)
  const desks = deskInstances.map((i) => i.code)
  const deskName = (c: string) => deskInstances.find((i) => up(i.code) === up(c))?.name
  const otherCodes = allUnits.flatMap((g) => g.instances.map((i) => i.code)).filter((c) => up(c) !== up(instance.code))
  const liveUnit = kind === 'service_point' ? undefined : data.liveUnit(instance.code)
  const liveDesk = kind === 'service_point' ? data.liveDesk(instance.code) : undefined
  const readOnly = !canWrite
  const mayKey = (session?.identity.permissions ?? []).includes('branch.manage')

  const wanted = code.trim().toUpperCase()
  const renamed = wanted !== instance.code.toUpperCase()
  const shapeBad = !codeShapeOk(wanted)
  const taken = !shapeBad && otherCodes.some((c) => c.toUpperCase() === wanted)
  const hasDesk = kind === 'desk_display' || kind === 'feedback'

  const next = (): UnitInstance => {
    const n: UnitInstance = { ...instance, code: wanted, name: name.trim(), overrides: own }
    if (hasDesk) {
      if (desk) n.desk = desk
      else delete n.desk
    }
    if (kind === 'display') {
      if (filter.length) n.deskFilter = filter
      else delete n.deskFilter
    }
    return n
  }
  const dirty = JSON.stringify(next()) !== JSON.stringify(instance)

  // Saved with the branch page's other edits; on a refusal the page stays, saying why.
  const saveWith = async (change: (x: BranchDoc) => BranchDoc, done: string) => {
    setAsking(false)
    setRefused('')
    const why = await d.commit(change)
    if (why) return setRefused(why)
    flash(done)
    passUnsaved()
    onClose()
  }

  const apply = () => {
    if (!dirty) return onClose()
    const n = next()
    const renamedFrom = renamed ? instance.code : undefined
    void saveWith((x) => {
      const base = renamedFrom && kind === 'service_point' ? renameDeskCode(x, renamedFrom, n.code) : x
      return {
        ...base,
        units: base.units.map((g, i) => (i !== gi ? g : { ...g, instances: g.instances.map((it, k) => (k === j ? n : it)) })),
      }
    }, say('common.saved'))
  }

  const remove = () =>
    void saveWith(
      (x) => ({
        ...x,
        units: x.units.map((g, i) => (i !== gi ? g : { ...g, instances: g.instances.filter((_, k) => k !== j) })),
      }),
      say('settings.live.removed'),
    )

  const from: Partial<Record<SettingField, InheritedFrom>> = Object.fromEntries(
    SETTING_FIELDS[kind].map((f) => [f, profileSettings[f] != null ? 'profile' : 'default']),
  )
  const fields = data.layouts ? designFields(kind) : []

  const deskChoices = desk && !desks.some((c) => up(c) === up(desk)) ? [...desks, desk] : desks
  const filterChoices = [...desks, ...filter.filter((c) => !desks.some((x) => up(x) === up(c)))]
  const toggle = (c: string) =>
    setFilter((f) => {
      const on = f.some((x) => up(x) === up(c))
      const n = on ? f.filter((x) => up(x) !== up(c)) : [...f, c]
      return filterChoices.filter((x) => n.some((y) => up(y) === up(x)))
    })

  // The live list is read every 10 s; the app form resets only when the placement changes.
  const appKey = liveUnit ? `${liveUnit.id} ${liveUnit.appId ?? ''} ${JSON.stringify(liveUnit.appAttributes ?? {})}` : ''
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const placed = useMemo(() => liveUnit, [appKey])
  const showApp = placed && (kind === 'kiosk' || kind === 'display' || kind === 'desk_display') && (data.moduleOn || placed.appId)
  const kindOne = say(`eq.kind.one.${kind}` as StaffKey)

  return (
    <InnerPage
      before={crumbs}
      title={instance.name}
      tag={
        <Chip size="sm" variant="secondary">
          {instance.code}
        </Chip>
      }
      line={pu.name === kindOne ? kindOne : `${kindOne} · ${pu.name}`}
      dirty={dirty}
      valid={!shapeBad && !taken && name.trim() !== ''}
      submitLabel={say('common.save')}
      busy={d.busy}
      error={refused || undefined}
      hideSubmit={readOnly}
      wide
      bare
      asForm={false}
      actions={
        !readOnly && (
          <Confirm
            ask={say('common.remove')}
            question={removeQuestion(retiring([instance], kind, data.live.counters ?? []), say)}
            yes={say('common.remove')}
            keep={say('common.keep')}
            busy={d.busy}
            onYes={remove}
          />
        )
      }
      onClose={onClose}
      onSubmit={() => (renamed && !asking ? setAsking(true) : apply())}
    >
      {asking && (
        <AskBar
          title={say('eq.inst.codeAsk')}
          actions={
            <>
              <Button variant="ghost" size="sm" autoFocus onPress={() => setAsking(false)}>
                {say('common.cancel')}
              </Button>
              <Button variant="primary" size="sm" isDisabled={d.busy} onPress={apply}>
                {say('common.save')}
              </Button>
            </>
          }
        />
      )}
      <EditLayout
        side={
          <>
            <SummaryCard
              band={kindOne}
              tile={<IconTile text={instance.code} size="xl" />}
              name={instance.name}
              sub={hasDesk && instance.desk ? deskLabel(instance.desk, deskName(instance.desk)) : undefined}
              state={
                <LiveStatus
                  kind={kind}
                  loaded={kind === 'service_point' ? data.live.counters !== null : data.live.units !== null}
                  unit={liveUnit}
                  desk={liveDesk}
                  timezone={branch.timezone}
                />
              }
            />
            {kind !== 'service_point' && (
              <Card className="bpm-side" role="region" aria-label={say('bpm.address')}>
                <span className="bpm-side__label">{say('bpm.address')}</span>
                <Address kind={kind} branchCode={branch.code} code={instance.code} />
              </Card>
            )}
            {mayKey && liveUnit && <ScreenKeySection unitId={liveUnit.id} timezone={branch.timezone} />}
          </>
        }
      >
        <FormSection id="bpm-machine" icon="machine" title={say('bpm.machine')}>
          <div className="bpm-fields">
            <Field label={say('common.code')}>
              <Text
                value={code}
                onChange={(v) => {
                  setCode(v.toUpperCase())
                  setAsking(false)
                }}
                disabled={readOnly}
                required
              />
            </Field>
            <Field label={say('common.name')}>
              <Text value={name} onChange={setName} disabled={readOnly} required />
            </Field>
          </div>
          {shapeBad && <p className="field-error">{say('eq.problem.codeFormat')}</p>}
          {taken && <p className="field-error">{say('eq.problem.codeTaken')}</p>}
          {hasDesk && (
            <Field label={say('eq.inst.desk')}>
              <Pick
                label={say('eq.inst.desk')}
                value={desk}
                onChange={setDesk}
                disabled={readOnly}
                placeholder={say('catalogue.units.chooseDesk')}
                options={deskChoices.map((c) => ({ value: c, label: deskLabel(c, deskName(c)) }))}
              />
            </Field>
          )}
          {kind === 'display' && (
            <Field label={say('eq.inst.deskFilter')} group>
              <div className="eq-inst__chips">
                <Toggles
                  label={say('eq.inst.deskFilter')}
                  options={filterChoices.map((c) => [c, deskLabel(c, deskName(c))] as const)}
                  value={filterChoices.filter((c) => filter.some((x) => up(x) === up(c)))}
                  disabled={readOnly}
                  onToggle={toggle}
                />
                {filter.length === 0 && <span className="eq-inst__hint">{say('eq.inst.allDesks')}</span>}
              </div>
            </Field>
          )}
        </FormSection>

        {fields.length > 0 && (
          <FormSection id="bpm-design" icon="designs" title={say('eq.sheet.design')}>
            <div className="bpm-designs">
              {fields.map((f) => (
                <DesignPicker
                  key={f}
                  size="lg"
                  md={machineDesign(f, kind, pu, own, defaults, data.layouts!, draft.operationProfileId)}
                  kind={kind}
                  label={say(f === 'ticketLayoutId' ? 'eq.sheet.ticket' : 'bpm.screen')}
                  branchName={branch.name}
                  disabled={readOnly}
                  onChange={(v) => setOwn((o) => withDesign({ ...instance, overrides: o }, f, v).overrides)}
                />
              ))}
            </div>
          </FormSection>
        )}

        <FormSection id="bpm-settings" icon="grid" title={say('bp.settings')}>
          <SettingsForm
            kind={kind}
            own={own}
            inherited={effective(defaults, profileSettings, {})}
            inheritedFrom={from}
            choices={designChoices(data.layouts ?? [], kind, draft.operationProfileId)}
            onChange={setOwn}
            readOnly={readOnly}
            omit={data.layouts ? ['layoutId', 'ticketLayoutId'] : []}
          />
        </FormSection>

        {showApp && (
          <FormSection id="bpm-app" icon="plug" title={say('bpm.app')}>
            <ScreenAppSection
              unit={placed}
              kind={kind === 'kiosk' ? 'kiosk' : 'display'}
              apps={data.apps}
              moduleOn={data.moduleOn}
              onSaved={() => void data.live.read()}
            />
          </FormSection>
        )}
      </EditLayout>
    </InnerPage>
  )
}
